/**
 * ProductionLogPage — отдельная страница «Лог сделок».
 *
 * Таблица всех production-проектов с:
 *  - amo_synced: ✅/⚠️ есть/нет привязки к AmoCRM
 *  - verified: ✅/⚠️ кнопкой toggle (только админ)
 *  - audit_count + кнопка «История» → AuditModal
 *  - error_count + статус-точка (зелёный/красный) + кнопка «Ошибки» → ErrorsModal
 *
 * Фильтры: поиск, only_errors, only_unverified.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { Icon } from "@/components/prototype/Icon";

interface Deal {
  id: number;
  client_name: string;
  location: string | null;
  status: string | null;
  amo_lead_id: number | null;
  amo_synced: boolean;
  verified: boolean;
  verified_at: string | null;
  verified_by_user_id: string | null;
  error_count: number;
  audit_count: number;
  last_audit_ts: string | null;
  updated_at: string | null;
}

interface AuditEntry {
  id: number;
  op: string;
  user_id: string | null;
  user_name: string | null;
  source: string | null;
  field_changes: Array<{ field: string; before: unknown; after: unknown }> | null;
  note_payload: Record<string, unknown> | null;
  ts: string;
}

interface ErrorEntry {
  id: number;
  severity: string;
  error_type: string;
  message: string;
  source: string | null;
  context: Record<string, unknown> | null;
  ts: string;
  resolved_at: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  in_progress: "В работе",
  awaiting: "Ожидает",
  completed: "Завершён",
  cancelled: "Отменён",
};

const SOURCE_LABEL: Record<string, string> = {
  manual_ui: "ручная правка",
  excel_import: "Excel-импорт",
  amo_sync: "AmoCRM-sync",
  messenger_slash: "из мессенджера",
  api: "API",
  unknown: "—",
};

function apiBase() {
  return (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
}

async function fetchDeals(filters: { q: string; only_errors: boolean; only_unverified: boolean }) {
  const params = new URLSearchParams();
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.only_errors) params.set("only_errors", "true");
  if (filters.only_unverified) params.set("only_unverified", "true");
  const res = await fetch(`${apiBase()}/production-log/deals?${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ items: Deal[]; total: number }>;
}

async function fetchAudit(dealId: number) {
  const res = await fetch(`${apiBase()}/production-log/${dealId}/audit`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ items: AuditEntry[]; total: number }>;
}

async function fetchErrors(dealId: number, includeResolved: boolean) {
  const params = includeResolved ? "?include_resolved=true" : "";
  const res = await fetch(`${apiBase()}/production-log/${dealId}/errors${params}`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ items: ErrorEntry[]; total: number }>;
}

async function toggleVerify(dealId: number, verified: boolean) {
  const res = await fetch(`${apiBase()}/production-log/${dealId}/verify`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ verified }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function resolveError(errorId: number) {
  const res = await fetch(`${apiBase()}/production-log/errors/${errorId}/resolve`, {
    method: "POST", credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

function fmtDt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso.includes("T") ? iso : iso.replace(" ", "T") + "Z").toLocaleString("ru-RU", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function ProductionLogPage() {
  const { user } = useAuth();
  const isSuper = !!user?.is_superuser;
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [onlyUnverified, setOnlyUnverified] = useState(false);
  const [auditDeal, setAuditDeal] = useState<Deal | null>(null);
  const [errorsDeal, setErrorsDeal] = useState<Deal | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const dealsQ = useQuery({
    queryKey: ["production-log/deals", debouncedQ, onlyErrors, onlyUnverified],
    queryFn: () => fetchDeals({ q: debouncedQ, only_errors: onlyErrors, only_unverified: onlyUnverified }),
    staleTime: 15_000,
  });

  const verifyMut = useMutation({
    mutationFn: (vars: { id: number; verified: boolean }) => toggleVerify(vars.id, vars.verified),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["production-log/deals"] }),
  });

  const items = dealsQ.data?.items ?? [];

  return (
    <div className="prodlog-page">
      <div className="prodlog-header">
        <h1>Лог сделок</h1>
        <div className="prodlog-summary">
          {items.length > 0 && `${items.length} сделок`}
        </div>
      </div>

      <div className="prodlog-filters">
        <div className="prodlog-search">
          <Icon name="search" size={14}/>
          <input
            type="search"
            placeholder="Имя клиента, адрес, номер Amo…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <label className="prodlog-toggle">
          <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)}/>
          <span>Только с ошибками</span>
        </label>
        <label className="prodlog-toggle">
          <input type="checkbox" checked={onlyUnverified} onChange={(e) => setOnlyUnverified(e.target.checked)}/>
          <span>Только не проверенные</span>
        </label>
      </div>

      <div className="prodlog-table-wrap">
        {dealsQ.isLoading && <div className="prodlog-empty">Загрузка…</div>}
        {dealsQ.error && <div className="prodlog-error">Ошибка: {(dealsQ.error as Error).message}</div>}
        {!dealsQ.isLoading && items.length === 0 && (
          <div className="prodlog-empty">Сделок не нашлось</div>
        )}
        {items.length > 0 && (
          <table className="prodlog-table">
            <thead>
              <tr>
                <th>Клиент</th>
                <th>Адрес</th>
                <th className="th-center">Статус</th>
                <th className="th-center">AmoCRM</th>
                <th className="th-center">Проверена</th>
                <th className="th-center">История</th>
                <th className="th-center">Ошибки</th>
                <th>Обновлено</th>
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr key={d.id}>
                  <td>
                    <a href={`/production/${d.id}`} className="prodlog-deal-link">
                      {d.client_name}
                    </a>
                  </td>
                  <td className="prodlog-loc">{d.location || "—"}</td>
                  <td className="th-center">
                    <span className={`prodlog-status prodlog-status-${d.status}`}>
                      {STATUS_LABEL[d.status ?? ""] ?? d.status ?? "—"}
                    </span>
                  </td>
                  <td className="th-center">
                    {d.amo_synced ? (
                      <span className="prodlog-pill prodlog-pill-ok" title={`Amo #${d.amo_lead_id}`}>
                        ✓ #{d.amo_lead_id}
                      </span>
                    ) : (
                      <span className="prodlog-pill prodlog-pill-warn">⚠️ нет</span>
                    )}
                  </td>
                  <td className="th-center">
                    <button
                      type="button"
                      className={`prodlog-verify-btn${d.verified ? " is-ok" : " is-warn"}`}
                      disabled={!isSuper || verifyMut.isPending}
                      onClick={() => verifyMut.mutate({ id: d.id, verified: !d.verified })}
                      title={isSuper
                        ? (d.verified ? "Снять проверку" : "Отметить проверенной")
                        : "Только для админа"}
                    >
                      {d.verified ? "✓ Проверена" : "⚠️ Не проверена"}
                    </button>
                  </td>
                  <td className="th-center">
                    <button
                      type="button"
                      className="prodlog-action-btn"
                      onClick={() => setAuditDeal(d)}
                    >
                      📜 {d.audit_count}
                    </button>
                  </td>
                  <td className="th-center">
                    <button
                      type="button"
                      className={`prodlog-action-btn prodlog-action-errors${d.error_count > 0 ? " is-bad" : " is-good"}`}
                      onClick={() => setErrorsDeal(d)}
                    >
                      {d.error_count > 0 ? `🔴 ${d.error_count}` : "🟢 0"}
                    </button>
                  </td>
                  <td className="prodlog-ts">{fmtDt(d.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {auditDeal && <AuditModal deal={auditDeal} onClose={() => setAuditDeal(null)}/>}
      {errorsDeal && <ErrorsModal deal={errorsDeal} onClose={() => setErrorsDeal(null)} isSuper={isSuper}/>}
    </div>
  );
}

function AuditModal({ deal, onClose }: { deal: Deal; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["production-log/audit", deal.id],
    queryFn: () => fetchAudit(deal.id),
  });
  const items = q.data?.items ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="prodlog-backdrop" onClick={onClose}>
      <div className="prodlog-modal" onClick={(e) => e.stopPropagation()}>
        <div className="prodlog-modal-head">
          <div>
            <div className="prodlog-modal-title">История изменений</div>
            <div className="prodlog-modal-sub">{deal.client_name} {deal.location && `· ${deal.location}`}</div>
          </div>
          <button type="button" className="prodlog-close" onClick={onClose}><Icon name="x" size={18}/></button>
        </div>
        <div className="prodlog-modal-body">
          {q.isLoading && <div className="prodlog-empty">Загрузка…</div>}
          {!q.isLoading && items.length === 0 && <div className="prodlog-empty">История пуста</div>}
          {items.map(a => (
            <div key={a.id} className="prodlog-audit-row">
              <div className="prodlog-audit-head">
                <span className={`prodlog-audit-op op-${a.op.toLowerCase()}`}>{a.op}</span>
                <span className="prodlog-audit-user">{a.user_name ?? a.user_id ?? "—"}</span>
                <span className="prodlog-audit-source">{SOURCE_LABEL[a.source ?? ""] ?? a.source ?? "—"}</span>
                <span className="prodlog-audit-ts">{fmtDt(a.ts)}</span>
              </div>
              {a.field_changes && a.field_changes.length > 0 && (
                <div className="prodlog-audit-changes">
                  {a.field_changes.map((c, i) => (
                    <div key={i} className="prodlog-change">
                      <span className="prodlog-change-field">{c.field}:</span>
                      <span className="prodlog-change-before">{String(c.before ?? "∅")}</span>
                      <span className="prodlog-change-arrow">→</span>
                      <span className="prodlog-change-after">{String(c.after ?? "∅")}</span>
                    </div>
                  ))}
                </div>
              )}
              {a.note_payload && (
                <div className="prodlog-audit-note">
                  {typeof a.note_payload.text === "string" && a.note_payload.text && (
                    <div>«{a.note_payload.text}»</div>
                  )}
                  {a.note_payload.removed != null && <div>Удалено: {JSON.stringify(a.note_payload.removed)}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorsModal({ deal, onClose, isSuper }: { deal: Deal; onClose: () => void; isSuper: boolean }) {
  const qc = useQueryClient();
  const [includeResolved, setIncludeResolved] = useState(false);
  const q = useQuery({
    queryKey: ["production-log/errors", deal.id, includeResolved],
    queryFn: () => fetchErrors(deal.id, includeResolved),
  });
  const items = q.data?.items ?? [];
  const allGreen = useMemo(() => items.filter(e => !e.resolved_at).length === 0, [items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const resolveMut = useMutation({
    mutationFn: (id: number) => resolveError(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["production-log/errors", deal.id] });
      qc.invalidateQueries({ queryKey: ["production-log/deals"] });
    },
  });

  return (
    <div className="prodlog-backdrop" onClick={onClose}>
      <div className={`prodlog-modal prodlog-errors-modal${allGreen ? " is-all-green" : " is-has-errors"}`} onClick={(e) => e.stopPropagation()}>
        <div className="prodlog-modal-head">
          <div>
            <div className="prodlog-modal-title">
              {allGreen ? "🟢 Ошибок нет" : `🔴 Активные ошибки`}
            </div>
            <div className="prodlog-modal-sub">{deal.client_name} {deal.location && `· ${deal.location}`}</div>
          </div>
          <button type="button" className="prodlog-close" onClick={onClose}><Icon name="x" size={18}/></button>
        </div>
        <div className="prodlog-modal-body">
          <label className="prodlog-toggle prodlog-toggle-inline">
            <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)}/>
            <span>Показать решённые</span>
          </label>
          {q.isLoading && <div className="prodlog-empty">Загрузка…</div>}
          {!q.isLoading && items.length === 0 && (
            <div className="prodlog-empty prodlog-empty-green">Ошибок по этой сделке нет</div>
          )}
          {items.map(e => (
            <div key={e.id} className={`prodlog-err-row sev-${e.severity}${e.resolved_at ? " is-resolved" : ""}`}>
              <div className="prodlog-err-head">
                <span className="prodlog-err-type">{e.error_type}</span>
                <span className="prodlog-err-source">{SOURCE_LABEL[e.source ?? ""] ?? e.source ?? "—"}</span>
                <span className="prodlog-err-ts">{fmtDt(e.ts)}</span>
                {e.resolved_at && <span className="prodlog-err-resolved">решено {fmtDt(e.resolved_at)}</span>}
              </div>
              <div className="prodlog-err-msg">{e.message}</div>
              {e.context && (
                <pre className="prodlog-err-ctx">{JSON.stringify(e.context, null, 2)}</pre>
              )}
              {isSuper && !e.resolved_at && (
                <button
                  type="button"
                  className="prodlog-err-resolve"
                  onClick={() => resolveMut.mutate(e.id)}
                  disabled={resolveMut.isPending}
                >
                  Отметить решённой
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
